import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Bell,
  Cake,
  AlertCircle,
  Star,
  Trophy,
  MessageSquare,
  Headphones,
} from 'lucide-react';
import { useApp } from '../context';
import { cn, timeAgo as formatTimeAgo } from '../lib/utils';
import { notificationsApi, NotificationHistoryItem } from '../lib/api';
import { registerCacheCleanup, hasChanged } from '../lib/moduleCache';

// ─── Notification type → icon/color mapping ─────────────────────────────────

const TYPE_CONFIG: Record<string, { icon: React.ElementType; bg: string; color: string }> = {
  overdue_alert: { icon: AlertCircle, bg: 'bg-[#fa8585]/10', color: 'text-[#fa8585]' },
  birthday_reminder: { icon: Cake, bg: 'bg-pink-50', color: 'text-pink-500' },
  daily_summary: { icon: Bell, bg: 'bg-amber-50', color: 'text-amber-500' },
  weekly_summary: { icon: Star, bg: 'bg-blue-50', color: 'text-blue-500' },
  achievement: { icon: Trophy, bg: 'bg-emerald-50', color: 'text-emerald-500' },
  gentle_nudge: { icon: MessageSquare, bg: 'bg-violet-50', color: 'text-violet-500' },
  support_reply: { icon: Headphones, bg: 'bg-teal-50', color: 'text-teal-500' },
  support_update: { icon: Headphones, bg: 'bg-teal-50', color: 'text-teal-500' },
};

const DEFAULT_CONFIG = { icon: Bell, bg: 'bg-slate-100', color: 'text-slate-500' };

// ─── Group history items by time ─────────────────────────────────────────────

function groupByTime(items: NotificationHistoryItem[]) {
  const now = new Date();
  const todayLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const weekStartLocal = new Date(todayLocal.getTime() - 6 * 24 * 60 * 60 * 1000);

  const today: NotificationHistoryItem[] = [];
  const week: NotificationHistoryItem[] = [];
  const earlier: NotificationHistoryItem[] = [];

  for (const item of items) {
    const sent = new Date(item.sentAt);
    // Compare using local calendar dates to avoid timezone drift
    const sentLocal = new Date(sent.getFullYear(), sent.getMonth(), sent.getDate());
    if (sentLocal >= todayLocal) today.push(item);
    else if (sentLocal >= weekStartLocal) week.push(item);
    else earlier.push(item);
  }

  return [
    { label: 'Today', key: 'today', items: today },
    { label: 'This Week', key: 'week', items: week },
    { label: 'Earlier', key: 'earlier', items: earlier },
  ].filter((g) => g.items.length > 0);
}

// Module-level cache so re-mounts don't flash loading spinner
let _notificationsCache: NotificationHistoryItem[] | null = null;
registerCacheCleanup(() => {
  _notificationsCache = null;
});

// ─── Component ───────────────────────────────────────────────────────────────

export default function Notifications() {
  const navigate = useNavigate();
  const { state } = useApp();

  const [history, setHistory] = useState<NotificationHistoryItem[]>(_notificationsCache || []);
  const [loading, setLoading] = useState(!state.isGuestMode && !_notificationsCache);

  // Track which unread IDs have become visible
  const pendingReadIds = useRef(new Set<number>());
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Fetch notification history for authenticated users
  useEffect(() => {
    if (state.isGuestMode) return;
    notificationsApi
      .history({ limit: 50 })
      .then((res) => {
        if (hasChanged(_notificationsCache, res.data)) setHistory(res.data);
        _notificationsCache = res.data;
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [state.isGuestMode]);

  // Flush pending mark-as-read IDs to API
  const flushRead = useCallback(() => {
    if (pendingReadIds.current.size === 0) return;
    const ids = Array.from(pendingReadIds.current);
    pendingReadIds.current.clear();
    notificationsApi.markAsRead(ids).catch(() => {});
  }, []);

  // Debounced flush — called when new IDs enter the visible set
  const scheduleFlush = useCallback(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(flushRead, 800);
  }, [flushRead]);

  // Flush on unmount (user leaves page)
  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      // Fire any remaining pending reads
      if (pendingReadIds.current.size > 0) {
        const ids = Array.from(pendingReadIds.current);
        notificationsApi.markAsRead(ids).catch(() => {});
      }
    };
  }, []);

  // Set up IntersectionObserver
  useEffect(() => {
    if (state.isGuestMode) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        let added = false;
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const id = Number(entry.target.getAttribute('data-notification-id'));
            const wasOpened = entry.target.getAttribute('data-was-opened') === 'true';
            if (id && !wasOpened && !pendingReadIds.current.has(id)) {
              pendingReadIds.current.add(id);
              added = true;
            }
          }
        }
        if (added) scheduleFlush();
      },
      { threshold: 0.5 },
    );

    return () => observerRef.current?.disconnect();
  }, [state.isGuestMode, scheduleFlush]);

  // Observe/unobserve notification cards via callback ref
  const cardRefCallback = useCallback(
    (el: HTMLDivElement | null) => {
      if (el && observerRef.current) observerRef.current.observe(el);
    },
    // Re-run when history changes so new cards get observed
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [history],
  );

  // ─── Guest mode: derive from local state ─────────────────────────────────

  if (state.isGuestMode) {
    return <GuestNotifications state={state} navigate={navigate} />;
  }

  // ─── Auth mode: API-driven ────────────────────────────────────────────────

  const groups = groupByTime(history);

  return (
    <div className="bg-[#f8f5f5] min-h-screen">
      {/* Header */}
      <div className="flex items-center gap-3 px-5 pt-4 pb-2 sticky top-0 bg-[#f8f5f5] z-10">
        <button
          onClick={() => navigate(-1)}
          className="size-10 flex items-center justify-center rounded-full hover:bg-white transition-colors"
        >
          <ArrowLeft size={22} className="text-slate-700" />
        </button>
        <h1 className="text-xl font-bold text-slate-900 flex-1">Notifications</h1>
      </div>

      <div className="px-5 pb-8 space-y-5 mt-2">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="size-8 border-3 border-[#fa8585]/30 border-t-[#fa8585] rounded-full animate-spin" />
          </div>
        ) : history.length === 0 ? (
          <EmptyState />
        ) : (
          groups.map((group) => (
            <div key={group.key}>
              <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest px-2 mb-3">
                {group.label}
              </h2>
              <div className="space-y-2">
                {group.items.map((notif) => {
                  const config = TYPE_CONFIG[notif.notificationType] || DEFAULT_CONFIG;
                  const Icon = config.icon;
                  const isClickable =
                    notif.metadata?.ticketId ||
                    notif.notificationType === 'support_reply' ||
                    notif.notificationType === 'support_update' ||
                    notif.contactId;
                  return (
                    <div
                      key={notif.id}
                      ref={cardRefCallback}
                      data-notification-id={notif.id}
                      data-was-opened={String(notif.wasOpened)}
                      onClick={() => {
                        if (notif.metadata?.ticketId) {
                          navigate(`/support/${notif.metadata.ticketId}`);
                        } else if (
                          notif.notificationType === 'support_reply' ||
                          notif.notificationType === 'support_update'
                        ) {
                          navigate('/help-support');
                        } else if (notif.contactId) {
                          navigate(`/contact/${notif.contactId}`);
                        }
                      }}
                      className={cn(
                        'flex items-start gap-4 p-4 rounded-2xl border transition-colors',
                        !notif.wasOpened
                          ? 'bg-white shadow-sm border-slate-100/50'
                          : 'bg-white/50 border-transparent',
                        isClickable && 'cursor-pointer active:scale-[0.98]',
                      )}
                    >
                      <div
                        className={cn(
                          'size-12 rounded-xl flex items-center justify-center shrink-0',
                          config.bg,
                        )}
                      >
                        <Icon size={24} className={config.color} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p
                            className={cn(
                              'text-sm leading-tight',
                              !notif.wasOpened
                                ? 'font-bold text-slate-900'
                                : 'font-semibold text-slate-600',
                            )}
                          >
                            {notif.title}
                          </p>
                          {!notif.wasOpened && (
                            <span className="size-2 rounded-full bg-[#fa8585] shrink-0 mt-1.5" />
                          )}
                        </div>
                        <p className="text-xs text-slate-400 mt-1 leading-relaxed">{notif.body}</p>
                        <p className="text-[10px] text-slate-300 mt-1.5 font-medium">
                          {formatTimeAgo(notif.sentAt)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ─── Guest Mode Notifications (local state) ──────────────────────────────────

function GuestNotifications({
  state,
  navigate,
}: {
  state: ReturnType<typeof useApp>['state'];
  navigate: ReturnType<typeof useNavigate>;
}) {
  const overdueContacts = state.contacts.filter((c) => c.status === 'overdue');
  const needsAttentionContacts = state.contacts.filter((c) => c.status === 'needs_attention');

  const upcomingBirthdays = React.useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return state.contacts
      .filter((c) => c.birthday)
      .map((contact) => {
        const [, month, day] = contact.birthday!.split('-');
        const bday = new Date(today.getFullYear(), parseInt(month) - 1, parseInt(day));
        if (bday < today) bday.setFullYear(today.getFullYear() + 1);
        const daysUntil = Math.ceil((bday.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        return { contact, daysUntil };
      })
      .filter((b) => b.daysUntil <= 14)
      .sort((a, b) => a.daysUntil - b.daysUntil);
  }, [state.contacts]);

  const todayNotifs = overdueContacts.map((c) => ({
    id: `overdue-${c.id}`,
    icon: AlertCircle,
    bg: 'bg-[#fa8585]/10',
    color: 'text-[#fa8585]',
    title: `${c.name} misses you`,
    desc: `You haven't connected in ${c.overdueDays} days. Send a quick message!`,
    time: 'Just now',
    unread: true,
  }));

  const weekNotifs = [
    ...upcomingBirthdays.map((b) => ({
      id: `birthday-${b.contact.id}`,
      icon: Cake,
      bg: 'bg-pink-50',
      color: 'text-pink-500',
      title:
        b.daysUntil === 0
          ? `${b.contact.name.split(' ')[0]}'s birthday is today!`
          : `${b.contact.name.split(' ')[0]}'s birthday in ${b.daysUntil} day${b.daysUntil === 1 ? '' : 's'}`,
      desc: b.daysUntil === 0 ? 'Send birthday wishes!' : 'Plan something special.',
      time: b.daysUntil === 0 ? 'Today' : `In ${b.daysUntil}d`,
      unread: b.daysUntil <= 1,
    })),
    ...needsAttentionContacts.slice(0, 3).map((c) => ({
      id: `attention-${c.id}`,
      icon: Bell,
      bg: 'bg-amber-50',
      color: 'text-amber-500',
      title: `Check in with ${c.name.split(' ')[0]}`,
      desc: `It's been a while — a quick message would help.`,
      time: '',
      unread: false,
    })),
  ];

  const allNotifs = [...todayNotifs, ...weekNotifs];
  const groups = [
    { label: 'Today', key: 'today', items: todayNotifs },
    { label: 'This Week', key: 'week', items: weekNotifs },
  ].filter((g) => g.items.length > 0);

  return (
    <div className="bg-[#f8f5f5] min-h-screen">
      <div className="flex items-center gap-3 px-5 pt-4 pb-2 sticky top-0 bg-[#f8f5f5] z-10">
        <button
          onClick={() => navigate(-1)}
          className="size-10 flex items-center justify-center rounded-full hover:bg-white transition-colors"
        >
          <ArrowLeft size={22} className="text-slate-700" />
        </button>
        <h1 className="text-xl font-bold text-slate-900 flex-1">Notifications</h1>
      </div>

      <div className="px-5 pb-8 space-y-5 mt-2">
        {allNotifs.length === 0 ? (
          <EmptyState />
        ) : (
          groups.map((group) => (
            <div key={group.key}>
              <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest px-2 mb-3">
                {group.label}
              </h2>
              <div className="space-y-2">
                {group.items.map((notif) => {
                  const Icon = notif.icon;
                  return (
                    <div
                      key={notif.id}
                      className={cn(
                        'flex items-start gap-4 p-4 rounded-2xl border transition-colors',
                        notif.unread
                          ? 'bg-white shadow-sm border-slate-100/50'
                          : 'bg-white/50 border-transparent',
                      )}
                    >
                      <div
                        className={cn(
                          'size-12 rounded-xl flex items-center justify-center shrink-0',
                          notif.bg,
                        )}
                      >
                        <Icon size={24} className={notif.color} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p
                            className={cn(
                              'text-sm leading-tight',
                              notif.unread
                                ? 'font-bold text-slate-900'
                                : 'font-semibold text-slate-600',
                            )}
                          >
                            {notif.title}
                          </p>
                          {notif.unread && (
                            <span className="size-2 rounded-full bg-[#fa8585] shrink-0 mt-1.5" />
                          )}
                        </div>
                        <p className="text-xs text-slate-400 mt-1 leading-relaxed">{notif.desc}</p>
                        <p className="text-[10px] text-slate-300 mt-1.5 font-medium">
                          {notif.time}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ─── Empty State ─────────────────────────────────────────────────────────────

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
      <Bell size={48} className="text-[#fa8585]/30 mb-4" />
      <h3 className="text-lg font-bold text-slate-900 mb-2">You're all caught up!</h3>
      <p className="text-slate-500 text-sm max-w-60 mx-auto">
        No notifications right now. We'll let you know when something needs your attention.
      </p>
    </div>
  );
}
